import {
  Form,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { LoaderData } from "./loader";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { PageNavigationHeader } from "@/features/license-fee/list/layouts/page-navigation-header";
import { Action, schema } from "./action";
import { getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { ComboboxDemo } from "@/components/ui/combobox";
import { usePackageSelection } from "@/hooks/usePackageSelection";
import { debounce } from "@/utils/debounce";
import { useEffect } from "react";
import { Input } from "@/components/inputs/input";
import { NumberInput } from "@/components/inputs/number-input/number-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MoneyInput } from "@/components/inputs/money-input/money-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/utils/format/currency";
import { formatInTimeZone } from "date-fns-tz";
import { Button } from "@/components/ui/button";
import { MonthPicker } from "@/components/inputs/month-input/month-picker";

export default function LicenseFeeEdit() {
  const actionData = useActionData() as Action;
  const {
    licenseFee,
    tierOptions,
    providerOptions,
    paymentMethodOptions,
    companyPackages,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [form, fields] = useForm({
    defaultValue: licenseFee,
    lastResult: actionData?.submission,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const { handleSearchPackages } = usePackageSelection({
    defaultPackages: companyPackages,
  });

  useEffect(() => {
    if (actionData?.success) {
      document.querySelector("#license-fee-edit")?.scrollIntoView();
    }
  }, [actionData?.success]);

  return (
    <div className="bg-slate-50" id="license-fee-edit">
      <div className="p-6 max-w-4xl mx-auto bg-slate-50 h-full gap-8 flex flex-col">
        <PageNavigationHeader
          onClick={() => navigate(DEFAULT_ROUTES.LICENSE_FEE_LIST)}
          title={`Editar registro de license fee de ${licenseFee.companyName}`}
          subtitle={
            <>
              Edite este registro de license fee.{" "}
              <span className="font-medium">
                Campos com asterísco * são obrigatórios.
              </span>
            </>
          }
        />

        <Form
          className="gap-8 flex flex-col"
          method="PATCH"
          id={form.id}
          onSubmit={form.onSubmit}
        >
          <div className="flex-col justify-start items-start gap-8 inline-flex">
            <div className="self-stretch flex-col justify-start items-start gap-px flex">
              <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
                Empresa
              </div>
              <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
                Você pode editar qual pacote esta associado a este license fee.
              </div>
            </div>

            <div className="self-stretch justify-start items-start gap-4 inline-flex">
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex cursor-not-allowed">
                <ComboboxDemo
                  key={licenseFee.companyId}
                  name={licenseFee.companyName}
                  selectPlaceholder={licenseFee.companyName || ""}
                  items={[]}
                  searchPlaceholder={""}
                  emptyPlaceholder={""}
                  disabled={true}
                  className="bg-gray-100 disabled:opacity-100"
                />
              </div>
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                <ComboboxDemo
                  key={fields.packageId.key}
                  name={fields.packageId.name}
                  emptyPlaceholder="Pacote não encontrado"
                  searchPlaceholder="Busque um pacote"
                  selectPlaceholder={`Pacote ID = ${licenseFee.packageId}`}
                  onChange={debounce(handleSearchPackages, 500)}
                  items={companyPackages}
                />
              </div>
            </div>
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
            <div className="self-stretch h-px border border-[#e3e3e7]" />
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-px flex">
            <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
              Configurações
            </div>
            <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Defina os principais parâmetros deste license fee.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 w-full">
            <Input
              label="Data de início *"
              className="col-span-1"
              showlabel
              {...getInputProps(fields.startDate, { type: "date" })}
              error={fields.startDate.errors}
            />
            <Input
              label="Data de fim *"
              className="col-span-1"
              showlabel
              {...getInputProps(fields.endDate, { type: "date" })}
              error={fields.endDate.errors}
            />

            <div>
              <Label>Tier</Label>
              <Select
                key={fields.tier.key}
                name={fields.tier.name}
                defaultValue={fields.tier.value as string}
              >
                <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                  <SelectValue placeholder="Selecione um tier" />
                </SelectTrigger>
                <SelectContent>
                  {tierOptions.map((tier) => (
                    <SelectItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <NumberInput
              label="Membros"
              showButtons={false}
              {...getInputProps(fields.members, { type: "number" })}
              error={fields.members.errors}
            />

            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea
                placeholder="Anote detalhes importantes sobre este license fee"
                className="h-32 bg-white border-[#d4d4d8] ring-inset"
                {...getInputProps(fields.notes, { type: "text" })}
              />
            </div>
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
            <div className="self-stretch h-px border border-[#e3e3e7]" />
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-px flex">
            <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
              Pagamento
            </div>
            <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Defina os detalhes de pagamento para este license fee.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 w-full">
            <div>
              <Label>Valor total em R$ *</Label>
              <MoneyInput
                label="Valor total em R$ *"
                placeholder="Digite o valor total"
                defaultValue={licenseFee.amount}
                disable={true}
                className="disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <Label>Isenção total em R$ *</Label>
              <MoneyInput
                label="Isenção total em R$ *"
                placeholder="Digite o valor da isenção"
                {...getInputProps(fields.exemptionAmount, { type: "text" })}
                error={fields.exemptionAmount.errors}
                disable={true}
                className="disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <NumberInput
                label="Parcelas *"
                placeholder="Digite o número de parcelas"
                showButtons={false}
                {...getInputProps(fields.installments, { type: "number" })}
                error={fields.installments.errors}
                disabled={true}
                style={{ cursor: "not-allowed" }}
              />
            </div>
            <div>
              <Label>Provedor *</Label>
              <Select
                key={fields.paymentProvider.key}
                name={fields.paymentProvider.name}
                defaultValue={fields.paymentProvider.value as string}
                disabled={true}
              >
                <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-gray-100 border-[#d4d4d8] ring-inset disabled:opacity-100 disabled:border-gray-200">
                  <SelectValue placeholder="Selecione um provedor" />
                </SelectTrigger>
                <SelectContent>
                  {providerOptions.map((provider) => (
                    <SelectItem
                      key={provider.value}
                      value={provider.value.toString()}
                    >
                      {provider.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Método *</Label>
              <Select
                key={fields.paymentMethod.key}
                name={fields.paymentMethod.name}
                defaultValue={fields.paymentMethod.value as string}
                disabled={true}
              >
                <SelectTrigger className="mt-1 w-full h-10 px-3 py-2 text-sm border rounded-md bg-gray-100 border-[#d4d4d8] ring-inset disabled:opacity-100 disabled:border-gray-200">
                  <SelectValue
                    placeholder="Selecione um método"
                    className="text-zinc-500"
                  />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethodOptions.map((paymentMethod) => (
                    <SelectItem
                      key={paymentMethod.value}
                      value={paymentMethod.value.toString()}
                    >
                      {paymentMethod.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Setup fee</Label>
              <MoneyInput
                label="Setup fee"
                placeholder="Digite o valor do setup fee"
                defaultValue={licenseFee.setupFeeAmount}
                disable={true}
                className="disabled:cursor-not-allowed"
              />
            </div>
          </div>
          <div className="self-stretch flex-col justify-start items-start gap-px flex">
            <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
              Cobranças
            </div>
            <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Informe as datas e revise as cobranças deste license fee.
            </div>
          </div>

          <div className="block w-full bg-white border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
            <Table className="border-collapse cursor-not-allowed ">
              <TableHeader className="h-[52px]">
                <TableRow>
                  <TableHead className="px-4 text-zinc-500 border-r border-zinc-200">
                    #
                  </TableHead>
                  <TableHead className="px-6 text-zinc-500 border-r border-zinc-200">
                    Data *
                  </TableHead>
                  <TableHead className="px-6 text-zinc-500 border-r border-zinc-200">
                    Competência *
                  </TableHead>
                  <TableHead className="px-6 text-zinc-500">
                    Valor em R$
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenseFee.payments
                  ?.sort((a, b) => (a.type === 9 ? -1 : b.type === 9 ? 1 : 0))
                  .map((installment) => (
                    <TableRow className="h-[52px]" key={installment.id}>
                      <TableCell className="px-4 border-r border-zinc-200">
                        {installment.id}
                      </TableCell>
                      <TableCell className="border-r border-zinc-200 p-2">
                        <Input
                          label="Data"
                          type="date"
                          className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none disabled:cursor-not-allowed disabled:bg-white"
                          defaultValue={
                            installment.paymentDate &&
                            formatInTimeZone(
                              installment.paymentDate,
                              "UTC",
                              "yyyy-MM-dd",
                            )
                          }
                          disable={true}
                        />
                      </TableCell>
                      <TableCell className="border-r border-zinc-200 p-2">
                        <MonthPicker
                          className="w-full h-full border-none ring-0 shadow-none focus:ring-0 focus:border-none disabled:cursor-not-allowed"
                          value={installment.competencyMonth}
                          onChange={(date) => {
                            installment.competencyMonth = date;
                          }}
                          disable={true}
                        />
                      </TableCell>
                      <TableCell className="p-2">
                        {
                          <div className="text-zinc-950 text-sm px-2">
                            {`${formatCurrency(installment.amount)} ${installment.type === 9 ? "(Setup fee)" : ""}`}
                          </div>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch h-px border border-[#e3e3e7]" />
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-px flex">
            <div className="self-stretch text-black text-lg font-medium font-['Inter'] leading-7">
              Outras Informações
            </div>
            <div className="self-stretch text-zinc-500 text-sm font-normal font-['Inter'] leading-tight">
              Informações adicionais do pacote.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 w-full">
            <Input
              label="Deal ID"
              className="col-span-1"
              showlabel
              placeholder="Digite o Deal ID"
              {...getInputProps(fields.dealId, { type: "text" })}
              error={fields.dealId.errors}
            />
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch h-px border border-[#e3e3e7]" />
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="px-4 py-2"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-4 py-2"
              disabled={navigation.state === "submitting"}
            >
              Salvar
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
